import { FC, useEffect } from 'react'
import { Routes } from '@/core/routes'
import useTranslation from 'next-translate/useTranslation'
import PageContentWrapper from '@/components/organisms/pageContentWrapper'
import { Box } from '@/components/atoms/Grid'
import ProductListingTitleCountInfo from '@/components/molecules/productListingTitleCountInfo'
import ProductListingPathHeader from '@/components/molecules/productListingPathHeader'
import {
  ProductListingFetcher,
  ProductListingFetcherResponse
} from '@/components/organisms/productListing/productListingFetcher'
import { Config } from '@/core/config'
import { convertAPIProductToProduct } from 'types/bridgeProduct'
import { ListingProducts, Sdk } from '@/network/graphql.g'
import useGraphQl from 'hooks/useGraphQl'
import { requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { useUser } from '@/providers/userProvider'
import { getCachableHeader } from '@/core/utils'
import useProductListing from 'hooks/useProductListing'
import { getProductListingData } from '@/components/organisms/productListing/productListingRedesign'
import Top100ProductsContainer from '@/components/organisms/top100ProductsContainer'
import { useMhub } from '@/providers/mhubProvider'
import { getMainData } from '@/utils/mainDataHelper'
import { SentryError } from '@/core/sentryError'
import { useNavigationSource } from '@/providers/navigationSourceProvider'
import FiltersProvider from '@/providers/filterProvider/filtersProvider'

type Top100Props = {
  top100Products: ListingProducts
  filterId: string
  requestId: string
  isPreload: boolean
}

const Top100: FC<Top100Props> = ({
  top100Products,
  filterId,
  requestId,
  isPreload
}) => {
  const { t } = useTranslation('common')
  const { isLoggedIn } = useUser()

  const { requestNotifyError } = useGraphQl()
  const fetcher: ProductListingFetcher = async (
    page,
    filterInput,
    refresh,
    initialLoad = false
  ): Promise<ProductListingFetcherResponse> => {
    const result = await requestNotifyError(
      (sdk: Sdk) => ({
        method: sdk.getNextProductsForTop100,
        variables: {
          limit: Config.perPage,
          page: page,
          withFilterAndProductsCount: refresh
        },
        requestHeaders: getCachableHeader(filterInput, isLoggedIn)
      }),
      true
    )
    if (!result.success) {
      throw (
        result.error ??
        new SentryError('Topproducts product listing', result.response)
      )
    }

    const { products, totalPagesCount } = result.response?.top100 ?? {}

    !initialLoad && pushSimpleTop100Page(page)

    return {
      products:
        products?.map((product) => convertAPIProductToProduct(product)) ?? [],
      totalPagesCount
    }
  }

  const { pushTop100Page, pushSimpleTop100Page } = useMhub()
  useEffect(() => {
    pushTop100Page()
  }, [])

  const { data } = useProductListing({
    totalPagesCount: top100Products.totalPagesCount,
    totalProductsCount: top100Products.totalProductsCount,
    id: 'top100',
    products: top100Products?.products,
    filterId,
    requestId,
    isPreload,
    fetcher
  })

  const { setNavigationSource } = useNavigationSource()

  useEffect(() => {
    setNavigationSource({
      from: 'topproducts',
      name: t('Top100.title')
    })
  }, [])

  return (
    <PageContentWrapper>
      <FiltersProvider>
        <Box>
          <ProductListingPathHeader
            title="title"
            paths={[
              {
                url: `${Routes.top100}`,
                title: t('Top100.title')
              }
            ]}
          />
          <ProductListingTitleCountInfo title={t('Top100.title')} />
          <Top100ProductsContainer
            productListingFetcher={fetcher}
            data={data}
          />
        </Box>
      </FiltersProvider>
    </PageContentWrapper>
  )
}

export default Top100

export const getServerSideProps = withBaseServerSideProps(
  async ({
    context,
    sdk,
    withMainNavigation,
    withStore,
    session
  }): Promise<GetServerSideProps<Top100Props>> => {
    const productListingData = getProductListingData(context)
    if ('redirect' in productListingData) {
      return {
        redirect: productListingData.redirect
      }
    }
    const { page, limit, filterInput, filterId, requestId, isPreload } =
      productListingData

    const { success, response } = await requestThrow401({
      method: sdk.getProductsForTop100,
      variables: {
        limit,
        page,
        withMainNavigation,
        withStore
      },
      requestHeaders: getCachableHeader(filterInput, session?.isLoggedIn),
      successPredicate: (response) => !!response?.top100
    })
    if (!success) {
      return {
        notFound: true
      }
    }

    if (response?.seoTags) {
      response.seoTags.image =
        response?.top100.products?.find((item) => item)?.mainImage?.url ?? null
    }

    return {
      props: {
        mainData: getMainData(response),
        seoTags: response?.seoTags,
        top100Products: response?.top100,
        filterId,
        requestId,
        isPreload,
        disableDaktela: true
      }
    }
  }
)
